import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Mail } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { campaignStatusMeta, CAMPAIGN_STATUSES } from '../../lib/outreach';
import FilterChip from '../../components/crm/FilterChip';
import EmptyState from '../../components/crm/EmptyState';
import CampaignWizard from '../../components/admin/CampaignWizard';

// `embedded` — rendered as the Sequences tab inside Communications: no page
// header of its own, the list + wizard behave the same.
export default function Outreach({ embedded = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Status filter lives in the URL (?status=...) so back-button returns to
  // the previously filtered view instead of resetting to "All".
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') || '';
  const setStatus = (next) => {
    const sp = new URLSearchParams(searchParams);
    if (next) sp.set('status', next);
    else sp.delete('status');
    setSearchParams(sp, { replace: false });
  };
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  // ?new_custom_for=<accountId> — deep link from AccountOutreachPanel's
  // "Create custom 1-school campaign" button. Opens the wizard pre-scoped to
  // that account; the param is cleared from the URL once consumed so a
  // refresh / back-navigation doesn't re-open the wizard.
  const [initialAccount, setInitialAccount] = useState(null);
  const newCustomFor = searchParams.get('new_custom_for') || '';
  useEffect(() => {
    if (!newCustomFor) return;
    const accountId = newCustomFor;
    // Clear the param first; this re-runs the effect with '' (no-op) so the
    // fetch below must not be gated on the param still being present.
    const sp = new URLSearchParams(searchParams);
    sp.delete('new_custom_for');
    setSearchParams(sp, { replace: true });
    api.get(`/api/admin/accounts/${accountId}`)
      .then((d) => setInitialAccount({ id: accountId, name: d?.account?.name || 'Account' }))
      .catch(() => setInitialAccount({ id: accountId, name: 'Account' }))
      .then(() => setNewOpen(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newCustomFor]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const sp = new URLSearchParams();
      if (status) sp.set('status', status);
      const res = await api.get(`/api/admin/outreach/campaigns?${sp.toString()}`);
      setCampaigns(res.campaigns || []);
    } catch (err) {
      setError(err?.message || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  if (user?.role !== 'nexsav_admin' && user?.role !== 'ops') {
    return <EmptyState title="Staff only" description="Outreach is restricted to Nexsav staff." />;
  }

  return (
    <div className={embedded ? '' : 'max-w-5xl mx-auto'} data-testid={embedded ? 'sequences-tab' : undefined}>
      <div className={`${embedded ? 'mb-4' : 'mb-6'} flex items-start justify-between flex-wrap gap-3`}>
        <div>
          {!embedded && <h1 className="text-xl md:text-2xl font-semibold text-white tracking-tight"><span className="serif-acc accent-text">Outreach</span></h1>}
          <p className={`text-[13px] ${embedded ? 'text-gray-400 max-w-[60ch]' : 'text-gray-500 mt-0.5'}`}>{embedded ? 'Multi-step drip sequences for cold outreach — enroll schools, and each step goes out on its schedule. For a one-time email to a set of schools, use Broadcasts.' : 'Campaigns sequence contacts through multi-step drip emails.'}</p>
        </div>
        <button onClick={() => { setInitialAccount(null); setNewOpen(true); }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900">
          <Plus className="w-3 h-3" /> New campaign
        </button>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <FilterChip active={!status} onClick={() => setStatus('')}>All</FilterChip>
        {CAMPAIGN_STATUSES.map((s) => (
          <FilterChip key={s} active={status === s} onClick={() => setStatus(s)}>{s}</FilterChip>
        ))}
      </div>

      {error && <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-3">{error}</div>}

      {!loading && campaigns.length === 0 ? (
        <EmptyState icon={Mail} title="No campaigns yet" description="Create one to start a multi-step outreach sequence." />
      ) : (
        <div className="space-y-2">
          {campaigns.map((c) => {
            const meta = campaignStatusMeta(c.status);
            return (
              <button key={c.id} onClick={() => navigate(`/admin/outreach/${c.id}`)}
                className="w-full text-left p-4 rounded-xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] transition-colors flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[14px] text-white font-medium">{c.name}</div>
                  {c.description && <div className="text-[12px] text-gray-500 mt-0.5">{c.description}</div>}
                </div>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${meta.tone}`}>
                  {meta.label}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <CampaignWizard
        open={newOpen}
        initialAccount={initialAccount}
        onClose={() => { setNewOpen(false); setInitialAccount(null); load(); }}
      />
    </div>
  );
}

